import React, { useEffect, useState } from 'react';

import { ReactComponent as TrendDownIcon } from '../../../assets/icons/dashboard/decrease-arrow-icon.svg';
import { ReactComponent as TrendUpIcon } from '../../../assets/icons/dashboard/increse-arrow-icon.svg';
import { ReactComponent as RevenueIcon } from '../../../assets/icons/dashboard/revenue-status-icon.svg';
import { ReactComponent as EnergyIcon } from '../../../assets/icons/dashboard/white-energy.svg';
import { ReactComponent as MultiUsersIcon } from '../../../assets/icons/sidebar/sidebar-account-active.svg';
import { ReactComponent as ActiveDevicesIcon } from '../../../assets/icons/sidebar/sidebar-device-active.svg';
import dashboard from '../../../utils/api/dashboard';

interface CardData {
    count: string | number;
    title: string;
    subTitle?: string;
    icon: React.ReactNode;
    trend: number;
    trendType: 'up' | 'down';
    iconBgColor: string;
    trendBgColor: string;
    trendColor: string;
    isLoading: boolean;
}

interface DashboardKPICardProperties {
    type: 'revenue' | 'energy' | 'activeDevices' | 'endConsumers';
    dateDuration?: string;
    isWidget?: boolean;
    filters?: any;
}

/**
 * Returns the default configuration for the given KPI type
 */
const getCardConfig = (type: string): CardData => {
    switch (type) {
        case 'energy':
            return {
                count: 0,
                title: 'Total Energy',
                subTitle: 'KWh',
                icon: <EnergyIcon />,
                trend: 8.2,
                trendType: 'up',
                iconBgColor: '#E0F2F1',
                trendBgColor: '#E0F2F1',
                trendColor: '#00695C',
                isLoading: true,
            };
        case 'activeDevices':
            return {
                count: 0,
                title: 'Active Devices',
                icon: <ActiveDevicesIcon />,
                trend: 4.1,
                trendType: 'up',
                iconBgColor: '#E0F2F1',
                trendBgColor: '#E0F2F1',
                trendColor: '#00695C',
                isLoading: true,
            };
        case 'endConsumers':
            return {
                count: 0,
                title: 'Total End Consumers',
                icon: <MultiUsersIcon />,
                trend: 0.5,
                trendType: 'down',
                iconBgColor: '#E0F2F1',
                trendBgColor: '#FFEBEE',
                trendColor: '#C62828',
                isLoading: true,
            };
        case 'revenue':
        default:
            return {
                count: 0,
                title: 'Total Revenue',
                subTitle: 'SAR',
                icon: <RevenueIcon />,
                trend: 12.5,
                trendType: 'up',
                iconBgColor: '#E0F2F1',
                trendBgColor: '#E0F2F1',
                trendColor: '#00695C',
                isLoading: true,
            };
    }
};

/**
 * A shared KPI card component that fetches and renders its own data
 */
function DashboardKPICard({ type, isWidget = false, dateDuration = '', filters = {}, }: DashboardKPICardProperties) {
    const [card, setCard] = useState<CardData>(getCardConfig(type));

    useEffect(() => {
        let isMounted = true;
        /**
         * Fetches the dashboard card data
         */
        const fetchCardData = async () => {
            try {
                setCard((previous) => ({ ...previous, isLoading: true }));
                const payload = {
                    timePeriod: dateDuration,
                    hardRefresh: true,
                    accountCustomId: filters?.facilityIds,
                    stationIds: filters?.stationIds,
                    deviceIds: filters?.deviceIds,
                    type: filters?.type,
                };
                const response = await dashboard.fetchDashboardCardDetails(payload);

                if (isMounted && response?.status === 200) {
                    const finalResult = response.data?.finalResult;

                    setCard((previous) => {
                        const newState = { ...previous, isLoading: false };
                        if (type === 'energy') {
                            const energyValue = finalResult?.electricity?.total || 0;
                            newState.count = (energyValue / 1000).toFixed(2);
                        } else if (type === 'revenue') {
                            newState.count = finalResult?.revenue?.total || 0;
                            newState.subTitle = finalResult?.revenue?.currency || 'SAR';
                        } else if (type === 'endConsumers') {
                            newState.count = finalResult?.users?.total || 0;
                        } else if (type === 'activeDevices') {
                            newState.count = finalResult?.electricity?.activeDevices || 0;
                        }
                        return newState;
                    });
                }
            } catch (error) {
                console.error('Error fetching KPI data', error);
                if (isMounted) {
                    setCard((previous) => ({ ...previous, isLoading: false }));
                }
            }
        };

        fetchCardData();

        return () => {
            isMounted = false;
        };
    }, [type, dateDuration]);

    const widgetStyles = isWidget ? { width: '100%', height: '100%', border: 'none', boxShadow: 'none' } : {};
    const bottomStyles = isWidget ? { marginTop: 'auto' } : {};

    return (
        <div className="new-dashboard-card" style={widgetStyles}>
            <div className="top-section">
                <h1 className="count">
                    {card.isLoading ? '...' : card.count && Number(card.count).toLocaleString()}{' '}
                    {card.subTitle && <span className="subtitle">{card.subTitle}</span>}
                </h1>
                <div className="icon-wrapper">
                    {card.icon}
                </div>
            </div>
            <div className="bottom-section" style={bottomStyles}>
                <p className="title">{card.title}</p>
                <div className="trend-wrapper" style={{ backgroundColor: card.trendBgColor, display: 'none' }}>
                    {card.trendType === 'up' ? <TrendUpIcon /> : <TrendDownIcon />}
                    <span style={{ color: card.trendColor }}>{card.trend}%</span>
                </div>
            </div>
        </div>
    );
}

DashboardKPICard.defaultProps = {
    dateDuration: '',
    isWidget: false,
    filters: {},
};

export default DashboardKPICard;
